/**
 * Navigator Tuning exposed parameters (feature 130): the Ranking weights and Ranking rules
 * groups as the panel shows them (pts = value × 100), shared by Plan detail and Review changes.
 */
import type { RankingConfigParams } from '../types/navigator-tuning.type';

export type Weight = keyof RankingConfigParams['weights'];
export type Rule = 'epsilon' | 'min_vector_score' | 'top_k' | 'tenure_cap';

export const WEIGHTS: { key: Weight; label: string; info: string; disabled?: boolean }[] = [
  {
    key: 'vector_similarity',
    label: 'Fit to the family’s needs',
    info: 'How closely the provider’s profile matches what the family described in the chat. This is the main signal: raising it puts fit ahead of credentials.',
  },
  {
    key: 'years_experience',
    label: 'Years of experience',
    info: 'Rewards providers with more years of professional experience, up to the experience cap.',
  },
  { key: 'years_in_business', label: 'Years in business', info: 'Rewards providers whose business has been operating longer.' },
  { key: 'certifications_array', label: 'Certifications', info: 'Rewards providers with professional certifications.' },
  { key: 'professional_licenses', label: 'Licenses', info: 'Rewards providers holding professional licenses.' },
  { key: 'professional_associations', label: 'Associations', info: 'Rewards membership in professional associations.' },
  { key: 'awards_recognitions', label: 'Awards', info: 'Rewards providers with industry awards or recognitions.' },
  {
    key: 'is_bquest_preferred',
    label: 'bQuest preferred',
    info: 'Boosts providers bQuest marked as preferred partners. Uses the provider’s current flag.',
  },
  {
    key: 'target_age_range',
    label: 'Target age',
    info: 'Meant to reward providers who serve the care recipient’s age group. It has no effect today because the recipient’s age isn’t collected, so it cannot be tuned yet.',
    disabled: true,
  },
];

export const RULES: { key: Rule; label: string; info: string; min: number; max: number; step: number; pts?: boolean }[] = [
  {
    key: 'epsilon',
    label: 'Near-tie window',
    info: 'Providers are ordered mainly by fit. When two are almost equally good fits, the one with stronger credentials goes first. This sets how close counts as “almost equal”, in fit points.',
    min: 0,
    max: 0.2,
    step: 0.01,
    pts: true,
  },
  {
    key: 'min_vector_score',
    label: 'Minimum fit to appear',
    info: 'The minimum fit (0–100) a provider needs to be shown at all. Higher means fewer but stronger matches; some specialties may end up empty.',
    min: 0,
    max: 1,
    step: 0.01,
    pts: true,
  },
  { key: 'top_k', label: 'Providers per specialty', info: 'The maximum number of providers listed under each specialty.', min: 1, max: 10, step: 1 },
  {
    key: 'tenure_cap',
    label: 'Experience cap (years)',
    info: 'Years of experience beyond this number add no extra points, so very long careers don’t outweigh everything else.',
    min: 1,
    max: 100,
    step: 1,
  },
];

export const pts = (n: number) => String(Math.round(n * 100));

export type ParamChange = { key: string; label: string; group: string; live: string; draft: string };

const weightLabel = (params: RankingConfigParams, key: Weight) => {
  const total = Object.values(params.weights).reduce((a, b) => a + b, 0) || 1;
  return `${pts(params.weights[key])} (${Math.round((params.weights[key] / total) * 100)}%)`;
};

/** Exposed parameters whose value differs, in panel order, formatted as the panel shows them. */
export function paramChanges(live: RankingConfigParams, draft: RankingConfigParams): ParamChange[] {
  const weights = WEIGHTS.filter((w) => live.weights[w.key] !== draft.weights[w.key]).map((w) => ({
    key: w.key,
    label: w.label,
    group: 'Ranking weights',
    live: weightLabel(live, w.key),
    draft: weightLabel(draft, w.key),
  }));
  const rules = RULES.filter((r) => live[r.key] !== draft[r.key]).map((r) => {
    const show = r.pts ? pts : String;
    return { key: r.key, label: r.label, group: 'Ranking rules', live: show(live[r.key]), draft: show(draft[r.key]) };
  });
  return [...weights, ...rules];
}

export const changesLabel = (n: number) => `${n} ${n === 1 ? 'change' : 'changes'}`;
